import { Directive, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { ConfigSettingsService, Deferred } from '@ebeihilfe/core';
import { IonicControllerService } from '@ebeihilfe/ionic-logic';
import { TranslateService } from '@ngx-translate/core';

import { EinreichungStep } from '../enums';
import { EinreichungStepResultType } from '../interfaces';
import { EinreichungService } from '../services';
import { Antrag } from '../types';

@Directive()
export class PopulateUnfinishedAntrag implements OnInit {
  private readonly configService = inject(ConfigSettingsService);
  private readonly einreichungService: EinreichungService = inject(EinreichungService);
  private readonly ionicController: IonicControllerService = inject(IonicControllerService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);

  private readonly _errorMessage: WritableSignal<string> = signal('');

  public antrag?: Antrag;
  public antragId = signal<string | null>(this.route.snapshot.params['antragId'] || null);
  public prio = EinreichungStep.INIT + 1;
  public showRetry = false;
  public errorMessage = this._errorMessage.asReadonly();

  protected work?: Deferred<EinreichungStepResultType>;

  public async canHandle(antrag: Antrag, antragId: string): Promise<boolean> {
    if (!this.configService.appSettings.einreichungV3Activated) {
      return !!(await this.einreichungService.checkForInteruptedAntrag());
    }

    const interuptedAntragId = await this.einreichungService.checkForInteruptedAntrag();

    if (!interuptedAntragId) {
      return false;
    }

    if (antragId) {
      return true;
    }

    const userConfirmationPromise = new Promise<boolean>((resolve) => {
      this.ionicController.showAlert({
        message: this.translate.instant(_('new_antrag.text.continue_antrag')),
        buttons: [
          {
            text: this.translate.instant(_('new_antrag.button.continue')),
            handler: async (): Promise<void> => {
              resolve(true);
            },
          },
          {
            text: this.translate.instant(_('new_antrag.button.new_antrag')),
            role: 'cancel',
            cssClass: 'secondary',
            handler: async () => {
              resolve(false);
            },
          },
        ],
      });
    });

    return await userConfirmationPromise;
  }

  public destroy(): Promise<void> {
    return Promise.resolve();
  }

  public hasUi(): boolean {
    return true;
  }

  public async ngOnInit(): Promise<void> {
    await this.populateFirstUnfinishedAntrag();
  }

  public async populateFirstUnfinishedAntrag(): Promise<void> {
    this.showRetry = false;
    this._errorMessage.set('');

    try {
      if (!this.antragId()) {
        const interuptedAntragId = await this.einreichungService.checkForInteruptedAntrag();
        this.antragId.set(interuptedAntragId || '');
      }

      this.antrag = await this.einreichungService.populateAntragFromAntragsInfo(this.antragId() || '');
    } catch (e: unknown) {
      if ((e as Error).message) {
        this._errorMessage.set((e as Error).message);
      }

      this.showRetry = true;
      return;
    }

    return this.finishStep();
  }

  private finishStep(): void {
    if (this.antrag && this.work) {
      this.work.resolve({
        result: this.antrag,
        shouldProceed: true,
      });
    }
  }
}
